import { useState, useEffect } from 'react';
import SettingsPageShell from './SettingsPageShell';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from '@/components/ui/dialog';
import { retailerSettings } from '../../utils/api';
import { validatePassword, passwordChecklist } from '../../utils/passwordPolicy';
import { toast } from 'sonner';
import { Lock, UserPlus, Trash2, Crown, User, Shield, Eye, EyeOff, Check, X } from 'lucide-react';

export default function RetailerSecurity({ user, onLogout }) {
  // Password
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);

  // Team
  const [team, setTeam] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ email: '', name: '', role: 'team_member' });
  const [inviteSaving, setInviteSaving] = useState(false);
  const [lastTempPassword, setLastTempPassword] = useState(null);

  const loadTeam = () => retailerSettings.listTeam().then((r) => setTeam(r.data)).catch(() => toast.error('Failed to load team'));
  useEffect(() => { loadTeam(); }, []);

  const submitPassword = async (e) => {
    e.preventDefault();
    const email = user?.email || '';
    const policyError = validatePassword(pw.next, { email });
    if (policyError) return toast.error(policyError);
    if (pw.next !== pw.confirm) return toast.error('New passwords do not match');
    setPwSaving(true);
    try {
      await retailerSettings.changePassword(pw.current, pw.next);
      toast.success('Password changed');
      setPw({ current: '', next: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwSaving(false);
    }
  };

  const submitInvite = async () => {
    if (!invite.email) return toast.error('Email is required');
    setInviteSaving(true);
    try {
      const res = await retailerSettings.inviteTeamMember(invite);
      toast.success(`Invited ${invite.email}`);
      setLastTempPassword(res.data.temporary_password);
      setInvite({ email: '', name: '', role: 'team_member' });
      setInviteOpen(false);
      loadTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invite failed');
    } finally {
      setInviteSaving(false);
    }
  };

  const removeMember = async (memberId, email) => {
    if (!window.confirm(`Remove ${email} from your team?`)) return;
    try {
      await retailerSettings.removeTeamMember(memberId);
      toast.success('Member removed');
      loadTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  return (
    <SettingsPageShell user={user} onLogout={onLogout} title="Security" subtitle="Password and team access">
      {/* Password */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-slate-700" /> Change Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submitPassword} className="space-y-4 max-w-md" autoComplete="off">
            {/* Username anchor — visually-hidden but DOM-present (display:none
                gets ignored by Chrome's autofill engine for username
                detection). Binds the post-change 'Update password?' prompt
                to the correct account email. */}
            <input
              type="email"
              name="username"
              value={user?.email || ''}
              autoComplete="username"
              readOnly
              tabIndex={-1}
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: '-9999px',
                width: '1px',
                height: '1px',
                opacity: 0,
                pointerEvents: 'none',
              }}
            />
            <div>
              <Label>Current Password</Label>
              <div className="relative">
                <Input
                  type={showCurrent ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={pw.current}
                  onChange={(e) => setPw({ ...pw, current: e.target.value })}
                  data-testid="current-password-input"
                  required
                />
                <button type="button" onClick={() => setShowCurrent((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>New Password</Label>
              <div className="relative">
                <Input
                  type={showNew ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={pw.next}
                  onChange={(e) => setPw({ ...pw, next: e.target.value })}
                  data-testid="new-password-input"
                  required
                  minLength={8}
                />
                <button type="button" onClick={() => setShowNew((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <ul className="text-xs space-y-1 bg-gray-50 rounded-md p-3 mt-2" data-testid="password-policy-list">
                {passwordChecklist(pw.next, { email: user?.email || '' }).map((rule) => (
                  <li
                    key={rule.label}
                    className={`flex items-start gap-2 ${rule.ok ? 'text-emerald-700' : 'text-gray-500'}`}
                  >
                    {rule.ok ? (
                      <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    ) : (
                      <X className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" />
                    )}
                    <span>{rule.label}</span>
                  </li>
                ))}
                <li className="flex items-start gap-2 text-gray-500 italic">
                  <span className="w-3.5 h-3.5" />
                  <span>Must not match your previous password</span>
                </li>
              </ul>
            </div>
            <div>
              <Label>Confirm New Password</Label>
              <Input
                type={showNew ? 'text' : 'password'}
                autoComplete="new-password"
                value={pw.confirm}
                onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                data-testid="confirm-password-input"
                required
              />
            </div>
            <Button type="submit" disabled={pwSaving} data-testid="change-password-submit" className="bg-slate-800 hover:bg-slate-900 text-white">
              {pwSaving ? 'Changing…' : 'Change Password'}
            </Button>
          </form>
          <div className="mt-6 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-700 flex items-start gap-2">
            <Shield className="w-4 h-4 text-slate-500 mt-0.5" />
            <span>Two-factor authentication (2FA) will be available in V1.2.</span>
          </div>
        </CardContent>
      </Card>

      {/* Team */}
      <Card className="border-0 shadow-md">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-700" /> Team Members
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">Team Leads can edit Settings; Team Members access only operational pages.</p>
          </div>
          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogTrigger asChild>
              <Button data-testid="invite-team-member-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <UserPlus className="w-4 h-4 mr-2" /> Invite Member
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Invite Team Member</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label>Email <span className="text-red-500">*</span></Label>
                  <Input type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} data-testid="invite-email-input" />
                </div>
                <div>
                  <Label>Name (optional)</Label>
                  <Input value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} data-testid="invite-name-input" />
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}>
                    <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="team_member">Team Member (operations only)</SelectItem>
                      <SelectItem value="team_lead">Team Lead (full access)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
                <Button onClick={submitInvite} disabled={inviteSaving} data-testid="invite-submit-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  {inviteSaving ? 'Sending…' : 'Send Invite'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {lastTempPassword && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
              <strong>Temporary password generated:</strong> <code className="bg-white px-1 rounded">{lastTempPassword}</code>
              <p className="text-xs mt-1">Share with the new member if they don't receive the email. They'll be asked to change it on first login.</p>
              <Button variant="link" size="sm" className="px-0 h-auto" onClick={() => setLastTempPassword(null)}>Dismiss</Button>
            </div>
          )}
          {!team ? (
            <div className="text-gray-500">Loading…</div>
          ) : (
            <div className="space-y-2">
              {team.members.map((m) => (
                <div key={m.id} data-testid={`team-row-${m.id}`} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-white border">
                      {m.role_in_team === 'team_lead' ? <Crown className="w-4 h-4 text-amber-500" /> : <User className="w-4 h-4 text-gray-500" />}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {m.name || m.email}
                        {m.is_self && <span className="text-xs text-gray-400 ml-2">(you)</span>}
                      </div>
                      <div className="text-xs text-gray-500">{m.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={m.role_in_team === 'team_lead' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}>
                      {m.role_in_team === 'team_lead' ? 'Team Lead' : 'Team Member'}
                    </Badge>
                    {!m.is_self && (
                      <Button variant="ghost" size="sm" onClick={() => removeMember(m.id, m.email)} data-testid={`team-remove-${m.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </SettingsPageShell>
  );
}
